import { type Fetcher, useFetchers } from '@remix-run/react'
import omit from 'lodash.omit'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { appConfig } from '~/app-config.ts'
import { type GetOrder } from '~/models/ecommerce-providers/sylius/shop/fetch/order/get-order.ts'
import { type GetOrderItem } from '~/models/ecommerce-providers/sylius/shop/fetch/order-item/get-order-item.ts'
import { type GetProductVariant } from '~/models/ecommerce-providers/sylius/shop/fetch/product-variant/get-product-variant.ts'
import { type OrderItemNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/order-item.ts'
import { CART_I18NEXT } from '~/routes/($lang).cart/cart.component.tsx'
import {
	PRODUCTS_SLUG_FORM_ADD_TO_CART,
	PRODUCTS_SLUG_INPUTS,
} from '~/routes/($lang).products.$slug/slug.component.tsx'
import { CartContext } from '~/routes/_layout'
import { ROOT_INPUTS } from '~/routes/_layout/root.component.tsx'

type UseCartProps = {
	cart: GetOrder
	isOptimistic?: boolean
}

function useCart(props: UseCartProps) {
	const { cart, isOptimistic = true } = props

	const { context } = React.useContext(CartContext)!
	const fetchers = useFetchers()
	const { t } = useTranslation(CART_I18NEXT.routeCart)

	let optimisticCount: number =
		(cart.items || []).reduce(
			(acc: number, item: GetOrderItem) => acc + (item.quantity || 0),
			0,
		) || 0

	let optimisticItemsPrice: number =
		cart.itemsTotal! - cart.orderPromotionTotal!
	let optimisticRemoved: GetOrderItem[] = []
	let optimisticItems: GetOrderItem[] = cart.items || []

	Object.entries(omit(context, 'undefined')).forEach(
		([
			,
			{ optimisticQuantity, optimisticDeleting, optimisticPrice, orderItemId },
		]) => {
			const item = optimisticItems.find(({ id }) => id == orderItemId)

			if (optimisticDeleting) {
				if (item) {
					optimisticRemoved.push(item)
					optimisticCount -= optimisticQuantity
					optimisticItemsPrice -= optimisticPrice
				}
			} else {
				optimisticCount += -(item?.quantity || 0) + optimisticQuantity
				optimisticItemsPrice +=
					-(
						(item?.discountedUnitPrice || item?.unitPrice || 0) *
						(item?.quantity || 0)
					) + optimisticPrice
			}
		},
	)

	let optimisticItemsTotal: number = optimisticItemsPrice
	let optimisticShippingTotal: number =
		optimisticItemsTotal < appConfig.free_shipping ? cart.shippingTotal! : 0
	let optimisticPromotionTotal: number = cart.orderPromotionTotal!
	let optimisticTotal: number =
		(optimisticItemsTotal || cart.total!) + optimisticShippingTotal
	let optimisticTaxTotal: number =
		(optimisticTotal * appConfig.tax) / (100 + appConfig.tax)

	const handleSubmission = ({ formData, data }: Fetcher) => {
		const optimistic = formData ? Object.fromEntries(formData.entries()) : data

		switch (optimistic?.[ROOT_INPUTS.subaction]) {
			case PRODUCTS_SLUG_FORM_ADD_TO_CART.subaction:
				const quantity = Number(optimistic[PRODUCTS_SLUG_INPUTS.quantity] || 1)

				if (
					!optimisticItems.find(
						({ variant }: OrderItemNormalized) =>
							(variant as GetProductVariant)['@id'] ===
							optimistic[PRODUCTS_SLUG_INPUTS.variantId],
					)
				) {
					optimisticItems = [
						{
							id: Number(optimistic[PRODUCTS_SLUG_INPUTS.variantId]),
							optimistic: true,
							variant: {
								lowestPriceBeforeDiscount: null,
								'@id': optimistic[PRODUCTS_SLUG_INPUTS.variantId] as string,
								images: [
									{
										path: optimistic[
											PRODUCTS_SLUG_INPUTS.variantImage
										] as string,
									},
								],
								optionValues: [
									...Array(
										optimistic[PRODUCTS_SLUG_INPUTS.variantOptionsValuesLength],
									).keys(),
								].map((i) => ({
									code: optimistic[
										`variantOptionsValues[${i}][code]`
									] as string,
									value: optimistic[
										`variantOptionsValues[${i}][value]`
									] as string,
									option: {
										name: optimistic[
											`variantOptionsValues[${i}][name]`
										] as string,
									},
								})),
								product: {
									slug: optimistic[PRODUCTS_SLUG_INPUTS.slug] as string,
								},
							},
							quantity,
							total: Number(optimistic[PRODUCTS_SLUG_INPUTS.variantPrice]),
							unitPrice: Number(optimistic[PRODUCTS_SLUG_INPUTS.variantPrice]),
							productName: optimistic[PRODUCTS_SLUG_INPUTS.name] as string,
						},
						...optimisticItems,
					]
				}
				break
		}
	}

	for (const f of fetchers) {
		handleSubmission(f)
	}

	return {
		optimisticItems,
		optimisticRemoved,
		optimisticCount,
		optimisticItemsTotal,
		optimisticShippingTotal,
		optimisticTaxTotal,
		optimisticPromotionTotal,
		optimisticTotal,
		t,
		...(!isOptimistic && {
			optimisticItems: cart.items,
			optimisticRemoved: [],
			optimisticCount:
				(cart.items || []).reduce(
					(acc: number, item: GetOrderItem) => acc + (item.quantity || 0),
					0,
				) || 0,
			optimisticItemsTotal: cart.itemsTotal! - cart.orderPromotionTotal!,
			optimisticShippingTotal: cart.shippingTotal,
			optimisticTaxTotal: cart.taxTotal,
			optimisticPromotionTotal: cart.orderPromotionTotal,
			optimisticTotal: cart.total,
		}),
	}
}

export { type UseCartProps, useCart }
